import Icon from '../../../components/AppIcon';

const MonthlyEarnings = ({ currentMonth, previousMonth, projection, level1Earnings, level2Earnings }) => {
  const monthlyChange = currentMonth && previousMonth ? ((currentMonth - previousMonth) / previousMonth) * 100 : 0;
  const isPositiveChange = monthlyChange >= 0;
  const totalCurrentEarnings = (level1Earnings || 0) + (level2Earnings || 0);

  return (
    <div className="bg-card rounded-lg shadow-card p-6 border border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-accent to-warning rounded-full flex items-center justify-center">
          <Icon name="TrendingUp" size={20} color="white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Ganancias Mensuales</h2>
          <p className="text-sm text-muted-foreground">Tu rendimiento en Bizznet Rewards</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-primary to-secondary p-5 rounded-lg text-primary-foreground">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm opacity-90">Mes Actual</span>
            <Icon name="Calendar" size={20} />
          </div>
          <div className="text-2xl font-bold mb-1">${currentMonth?.toLocaleString('es-CO')}</div>
          <div className="text-xs opacity-80">COP ganados en {new Date()?.toLocaleDateString('es-CO', { month: 'long' })}</div>
        </div>

        <div className="bg-gradient-to-br from-accent to-warning p-5 rounded-lg text-accent-foreground">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm opacity-90">Comparativa</span>
            <Icon name={isPositiveChange ? 'TrendingUp' : 'TrendingDown'} size={20} />
          </div>
          <div className="text-2xl font-bold mb-1">
            {isPositiveChange ? '+' : ''}{monthlyChange?.toFixed(1)}%
          </div>
          <div className="text-xs opacity-80">vs mes anterior (${previousMonth?.toLocaleString('es-CO')})</div>
        </div>

        <div className="bg-gradient-to-br from-success to-success/80 p-5 rounded-lg text-success-foreground">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm opacity-90">Proyección</span>
            <Icon name="Target" size={20} />
          </div>
          <div className="text-2xl font-bold mb-1">${projection?.toLocaleString('es-CO')}</div>
          <div className="text-xs opacity-80">Estimado fin de mes</div>
        </div>
      </div>

      <div className="mt-5 bg-muted/30 p-5 rounded-lg">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          Desglose por Niveles
        </h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="text-sm font-medium text-foreground">Nivel 1 (Referidos Directos)</span>
              </div>
              <span className="text-lg font-bold text-foreground">${level1Earnings?.toLocaleString('es-CO')}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-primary to-primary/80 h-full rounded-full transition-all duration-500"
                style={{ width: `${totalCurrentEarnings > 0 ? (level1Earnings / totalCurrentEarnings) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalCurrentEarnings > 0 ? ((level1Earnings / totalCurrentEarnings) * 100)?.toFixed(1) : 0}% del total mensual
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-secondary rounded-full"></div>
                <span className="text-sm font-medium text-foreground">Nivel 2 (Referidos Indirectos)</span>
              </div>
              <span className="text-lg font-bold text-foreground">${level2Earnings?.toLocaleString('es-CO')}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-secondary to-secondary/80 h-full rounded-full transition-all duration-500"
                style={{ width: `${totalCurrentEarnings > 0 ? (level2Earnings / totalCurrentEarnings) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalCurrentEarnings > 0 ? ((level2Earnings / totalCurrentEarnings) * 100)?.toFixed(1) : 0}% del total mensual
            </p>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Total Mes Actual</span>
            <span className="text-xl font-bold text-foreground">${totalCurrentEarnings?.toLocaleString('es-CO')} COP</span>
          </div>
        </div>
      </div>

      <div className="mt-5 bg-primary/5 border border-primary/20 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Cómo se calculan tus ganancias</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• <strong>Nivel 1:</strong> Recibes 20% de comisión por cada plan que active un referido directo</li>
              <li>• <strong>Nivel 2:</strong> Recibes 10% de comisión por cada plan que activen los referidos de tus referidos</li>
              <li>• <strong>Proyección:</strong> Basada en tu promedio diario del mes actual</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyEarnings;